import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api';

export interface QueueStat {
  name: string;
  waiting: number;
  active: number;
  delayed: number;
  failed: number;
  completed: number;
}

export interface FailedJob {
  queue: string;
  jobId: string;
  name: string;
  failedReason: string;
  timestamp: number;
  data: unknown;
}

export interface CoverageStats {
  reporters: {
    total: number;
    withEmail: number;
    withBio: number;
    withTwitter: number;
    withLinkedin: number;
    enriched: number;
    pending: number;
    failed: number;
    emailPct: number;
    enrichedPct: number;
  };
  publications: {
    total: number;
    withRss: number;
    withEmailFormats: number;
    enriched: number;
    pending: number;
    failed: number;
    rssPct: number;
    enrichedPct: number;
  };
}

export interface ApiCostRow {
  provider: string;
  requestType: string;
  callCount: number;
  totalCost: string;
}

export interface LlmCostRow {
  model: string;
  provider: string;
  requestType: string;
  callCount: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalCost: string;
}

export interface CostStats {
  apiUsage: { byType: ApiCostRow[]; total: string };
  llmUsage: { byModel: LlmCostRow[]; total: string };
  grandTotal: string;
  recentApiLogs: unknown[];
  recentLlmLogs: unknown[];
}

export function useQueueStats() {
  return useQuery<{ queues: QueueStat[] }>({
    queryKey: ['admin', 'queues'],
    queryFn: () => apiGet('/admin/queues'),
    refetchInterval: 15_000,
  });
}

export function useFailedJobs() {
  return useQuery<{ failedJobs: FailedJob[] }>({
    queryKey: ['admin', 'failed-jobs'],
    queryFn: () => apiGet('/admin/failed-jobs'),
    refetchInterval: 30_000,
  });
}

export function useCoverageStats() {
  return useQuery<CoverageStats>({
    queryKey: ['admin', 'coverage'],
    queryFn: () => apiGet('/admin/coverage'),
    refetchInterval: 60_000,
  });
}

export function useCostStats() {
  return useQuery<CostStats>({
    queryKey: ['admin', 'costs'],
    queryFn: () => apiGet('/admin/costs'),
    refetchInterval: 60_000,
  });
}

export function useEnrichReporter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reporterId: string) =>
      apiPost(`/admin/enrich/reporter/${reporterId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'queues'] });
    },
  });
}

export function useEnrichPublication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicationId: string) =>
      apiPost(`/admin/enrich/publication/${publicationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'queues'] });
    },
  });
}
